<script setup lang="ts">
// Pill-style segmented control for a relative-date preset
// (e.g. "Last 7 days", "MTD", "All time"). Authors translate the
// returned key into concrete dates in their SQL — this component
// just owns the selection UX.

import { computed, useId } from "vue";

import type { RelativeDateOpts, RelativeDatePreset } from "../sbi/protocol";
import { usePrintMode } from "../sbi/print-mode.ts";
import PrintStaticControl from "./PrintStaticControl.vue";
import ControlLabel from "./ControlLabel.vue";

const props = defineProps<{
  id: string;
  value: string;
  presets: ReadonlyArray<RelativeDatePreset<string>>;
  opts?: RelativeDateOpts;
}>();

const emit = defineEmits<{
  (e: "change-target", id: string, value: string): void;
}>();

const isPrint = usePrintMode();

// Label wiring: ControlLabel puts `labelId` on the label text.
const labelId = useId();

// Print-mode display: the selected preset's user-visible label. Falls
// back to the raw value if no preset matches — shouldn't happen in
// practice (the value is always one of the preset values), but keeps
// the print render honest if the worker emits a state we don't know.
const printValue = computed(() => {
  const match = props.presets.find((p) => p.value === props.value);
  return match?.label ?? String(props.value);
});

function pick(preset: RelativeDatePreset<string>): void {
  if (preset.value === props.value) return;
  emit("change-target", props.id, preset.value);
}
</script>

<template>
  <PrintStaticControl
    v-if="isPrint()"
    :label="opts?.label"
    :value="printValue"
  />
  <div v-else class="relative-date">
    <ControlLabel
      v-if="opts?.label || opts?.description"
      class="relative-date-label"
      :label="opts?.label"
      :description="opts?.description"
      :label-id="labelId"
    />
    <div
      class="pills"
      role="radiogroup"
      :aria-labelledby="opts?.label ? labelId : undefined"
    >
      <!--
        Empty-state branch — `presets` built dynamically from a query
        that returned zero rows. Without this the pill strip renders as
        a bare bordered box with nothing in it. Mirrors the muted
        italic empty line in `InsightCheckboxGroup.vue`.
      -->
      <span v-if="presets.length === 0" class="empty">No options</span>
      <button
        v-for="p in presets"
        :key="p.value"
        type="button"
        role="radio"
        :aria-checked="p.value === value"
        :class="['pill', { selected: p.value === value }]"
        @click="pick(p)"
      >
        {{ p.label }}
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Never wider than the slot it sits in; full width when that slot is
   narrow (filter bar < 480px, a narrow column cell). */
.relative-date {
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
@container (max-width: 479px) {
  .relative-date {
    display: flex;
    width: 100%;
  }
}
.relative-date-label {
  color: var(--color-text-muted);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.pills {
  max-width: 100%;
  box-sizing: border-box;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  background: var(--card);
  border: 1px solid var(--rule);
  border-radius: var(--radius);
  padding: 2px;
}
.pill {
  background: transparent;
  color: var(--card-ink);
  border: 0;
  border-radius: calc(var(--radius) - 2px);
  padding: 4px 10px;
  font: inherit;
  cursor: pointer;
  touch-action: manipulation;
}
.pill:hover {
  background: var(--rule);
}
.pill.selected {
  background: var(--primary);
  color: var(--card);
}
.pill:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 1px;
}
.empty {
  color: var(--color-text-muted);
  font-style: italic;
  font-size: 12px;
  padding: 4px 10px;
}
/* Touch: 16px text (iOS zooms the page when a smaller input takes
   focus) and 40–44px targets. Desktop keeps the 13px density. */
@media (pointer: coarse) {
  .pill {
    min-height: 40px;
    padding: 8px 14px;
    font-size: 16px;
  }
}
</style>
